import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CloneQuoteData, QuoteData } from '@iqd/shared-quoting';
import { maxValueValidator, minValueValidator } from '@iqd/shared-utils';
import { Store } from '@ngrx/store';
import { Apollo } from 'apollo-angular';
import { filter, find, includes, sortBy } from 'lodash-es';
import {
  combineLatest,
  map,
  Observable,
  ReplaySubject,
  Subscription,
} from 'rxjs';
import { AppState } from '../../app.module';
import { getAreaCodes, getLoadTypes } from './budget-form.model';

export function validateArea(maxArea: number): any {
  return (group: FormGroup) =>
    group.value.width * group.value.length <= maxArea
      ? null
      : {
          validateArea: {
            valid: false,
          },
        };
}

@Component({
  selector: 'app-budget-form',
  templateUrl: './budget-form.component.html',
  styleUrls: ['./budget-form.component.scss'],
})
export class BudgetFormComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  quoteData: QuoteData;
  @Output()
  quoteDataChange = new EventEmitter<any>();
  @Output()
  reset: EventEmitter<any> = new EventEmitter();
  @Output()
  submit: EventEmitter<any> = new EventEmitter();
  @Output()
  success: EventEmitter<any> = new EventEmitter();

  form: FormGroup;
  private settings: Observable<any>;
  private formChangesSub: Subscription;
  areaCodes: Observable<any[]>;
  loadTypeDefaults: Observable<any[]>;
  private selectedLoadTypeId = new ReplaySubject<number>(1);
  loadTypeDescription: Observable<string>;

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    private apollo: Apollo
  ) {
    this.settings = this.store.select(
      (state) => state.settings.currentSettings
    );
    this.areaCodes = this.getAreaCodes();
    this.loadTypeDefaults = this.getLoadTypes();
    this.loadTypeDescription = this.selectedLoadTypeId.pipe(
      (loadTypeId) =>
        combineLatest([loadTypeId, this.loadTypeDefaults, this.settings]),
      map((data) => {
        const loadTypes = data[1];
        const settings = data[2];
        const selectedLoadType = find(loadTypes, (lt) => lt.id === data[0]);
        return settings.get(
          `loadTypeDescription${selectedLoadType.description}`
        );
      })
    );
  }

  private getLoadTypes(): Observable<any> {
    return this.apollo
      .watchQuery<any>({ query: getLoadTypes })
      .valueChanges.pipe(
        map(({ data }) => {
          return sortBy(
            filter(data.loadTypeDefaults, (loadType) => {
              return includes(['Storage', 'Office'], loadType.description);
            }),
            'description'
          );
        })
      );
  }

  private getAreaCodes(): Observable<any> {
    return this.apollo
      .watchQuery<any>({ query: getAreaCodes })
      .valueChanges.pipe(map(({ data }) => data.areaCodes));
  }

  ngOnInit(): void {
    this.createForm();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.form && changes.quoteData) {
      this.updateForm();
    }
  }

  ngOnDestroy(): void {
    if (this.formChangesSub) {
      this.formChangesSub.unsubscribe();
    }
  }

  private createForm(): void {
    this.form = this.fb.group(
      {
        site: [
          this.quoteData.GeneralData.AreaCode || null,
          Validators.required,
        ],
        length: [
          this.quoteData.FloorData.GridData.XDim,
          [Validators.required, minValueValidator(1)],
        ],
        width: [
          this.quoteData.FloorData.GridData.YDim,
          [Validators.required, minValueValidator(1)],
        ],
        loadType: [
          this.quoteData.FloorData.TiersData.TierData[0].LoadType,
          Validators.required,
        ],
        height: [
          this.quoteData.FloorData.TiersData.TierData[0].Height,
          [Validators.required, minValueValidator(1), maxValueValidator(10)],
        ],
        fireProtection: [this.quoteData.GeneralData.IsFireRated],
        recaptcha: [null, Validators.required],
      },
      {
        validator: validateArea(1000),
      }
    );
    this.selectedLoadTypeId.next(Number(this.form.value.loadType));
    this.formChangesSub = this.form.valueChanges.subscribe((data) =>
      this.pushUpdates()
    );
  }

  private updateForm(): void {
    this.form.patchValue(
      {
        site: this.quoteData.GeneralData.AreaCode || null,
        length: this.quoteData.FloorData.GridData.XDim,
        width: this.quoteData.FloorData.GridData.YDim,
        loadType: this.quoteData.FloorData.TiersData.TierData[0].LoadType,
        height: this.quoteData.FloorData.TiersData.TierData[0].Height,
        fireProtection: this.quoteData.GeneralData.IsFireRated,
      },
      { emitEvent: false }
    );
    this.selectedLoadTypeId.next(Number(this.form.value.loadType));
  }

  onGetBudgetClick(): void {
    this.submit.emit();
  }

  onResetClick(): void {
    this.reset.emit();
  }

  _onSuccess(event: any): void {
    this.success.emit(event);
  }

  private pushUpdates(): void {
    const value = this.form.getRawValue();
    const qd = CloneQuoteData(this.quoteData);
    this.selectedLoadTypeId.next(Number(value.loadType));
    qd.GeneralData.AreaCode = Number(value.site);
    qd.GeneralData.IsFireRated = value.fireProtection;
    qd.FloorData.GridData.XDim = value.length;
    qd.FloorData.GridData.YDim = value.width;
    qd.FloorData.TiersData.TierData[0].LoadType = Number(value.loadType);
    qd.FloorData.TiersData.TierData[0].Height = value.height;
    qd.FloorData.TiersData.TierData[0].CutoutData = [];
    qd.FloorData.TiersData.TierCount = 1;
    qd.GeneralData.IsFireRated
      ? this.addFireProtection(qd)
      : this.removeFireProtection(qd);
    this.updateAncillaries(qd);
    this.quoteDataChange.emit(qd);
  }

  private addFireProtection(quoteData: QuoteData): void {
    quoteData.GeneralData.IsFireRated = true;
    quoteData.FireRatingData.CostFireRating = true;
    quoteData.FireRatingData.CostCeilings = true;
    quoteData.FireRatingData.CostFascia = true;
    quoteData.FireRatingData.CostColumnCasings = true;
  }

  private removeFireProtection(quoteData: QuoteData): void {
    quoteData.GeneralData.IsFireRated = false;
    quoteData.FireRatingData.CostFireRating = false;
    quoteData.FireRatingData.CostCeilings = false;
    quoteData.FireRatingData.CostFascia = false;
    quoteData.FireRatingData.CostColumnCasings = false;
  }

  private updateAncillaries(quoteData: QuoteData): void {
    for (const ancillary of quoteData.AncillaryData.AncillaryArguments) {
      if (ancillary.Height > 0) {
        ancillary.Height = quoteData.FloorData.TiersData.TierData[0].Height;
      } else if (ancillary.Length > 0) {
        ancillary.Length =
          quoteData.FloorData.GridData.XDim * 2 +
          quoteData.FloorData.GridData.YDim * 2;
      }
    }
  }
}
